import { useCallback, useMemo } from 'react';

import { ViableAccountsType } from '@givebacks/api/core';
import { useTransferCashback } from '@givebacks/api/ledger';
import { transformCurrency } from '@givebacks/utils/currency';
import { Alert, ButtonMantine, CurrencyInput, Stack, Title, notifications } from 'ui';
import { UseFormReturnType, useForm } from 'ui/Form';

import { useFormListValidation, useFormListValidator } from '../../../../hooks';
import {
  ChoosePaymentAccount,
  UseChoosePaymentAccountForm,
} from '../../../molecules/forms/ChoosePaymentAccount/ChoosePyamentAccount';

type TransferCashbackForm = {
  choose_payment_accounts?: UseChoosePaymentAccountForm;
  transfer_amount?: UseTransferCashBackInputForm;
};

type TransferCashBackInputForm = {
  transferAmount: number | string;
};

type UseTransferCashBackInputForm = UseFormReturnType<TransferCashBackInputForm>;

const TransferCashBackFormButton = ({ onTransferSuccess }: { onTransferSuccess: () => void }) => {
  const { validateForms, useGetForm } = useFormListValidation<TransferCashbackForm>();
  const { mutate: transferCashback, isPending: isLoading } = useTransferCashback();

  const choosePaymentAccountsForm = useGetForm('choose_payment_accounts');
  const transferAmountForm = useGetForm('transfer_amount');

  const handleStartTransfer = useCallback(() => {
    const { isValid } = validateForms();

    if (!isValid) return;

    const account = choosePaymentAccountsForm?.values.paymentAccount;
    const transferAmount = transferAmountForm?.values.transferAmount;

    if (!account || !transferAmount) return;

    transferCashback(
      {
        accountType: account,
        entry: {
          amount: -Math.abs(Number(transferAmount)),
        },
      },
      {
        onSuccess() {
          notifications.show({
            title: 'Transfer Successful',
            message: 'Your transfer was successful',
            variant: 'success',
          });
          onTransferSuccess();
        },
        onError() {
          notifications.show({
            title: 'Transfer Failed',
            message: 'An error occurred while processing your transfer',
            variant: 'error',
          });
          transferAmountForm.setFieldError('transferAmount', true);
        },
      }
    );
  }, [
    choosePaymentAccountsForm?.values.paymentAccount,
    onTransferSuccess,
    transferAmountForm,
    transferCashback,
    validateForms,
  ]);

  return (
    <ButtonMantine onClick={handleStartTransfer} loading={isLoading}>
      Transfer
    </ButtonMantine>
  );
};

const TransferCashBackInputForm = () => {
  const form = useForm<TransferCashBackInputForm>({
    initialValues: {
      transferAmount: 0,
    },
    validate: {
      transferAmount: (value) => (Number(transformCurrency(value, 'dollars')) < 25 ? true : null),
    },
  });

  useFormListValidator({ id: 'transfer_amount', form: form });

  const isErroredLessThanMinimum = useMemo(() => {
    if (
      Boolean(form.errors['transferAmount']) &&
      Number(transformCurrency(form.values.transferAmount, 'dollars')) < 25
    ) {
      return true;
    }
    return false;
  }, [form.errors, form.values.transferAmount]);
  return (
    <Stack gap="xs">
      <CurrencyInput {...form.getInputProps('transferAmount')} placeholder="Enter amount" />
      <Alert.Collapse type="error" in={isErroredLessThanMinimum} title="$25.00 minimum transfer" />
    </Stack>
  );
};

type TransferCashBackFormProps = {
  onConnectAccount: (account: ViableAccountsType) => void;
  onRemove?: (account: ViableAccountsType, accountId: string) => void;
  onTransferSuccess: () => void;
};

const TransferCashBackForm = ({
  onConnectAccount,
  onRemove,
  onTransferSuccess,
}: TransferCashBackFormProps) => {
  return (
    <Stack gap="lg">
      <Stack>
        <Title order={4}>Transfer Amount</Title>
        <TransferCashBackInputForm />
      </Stack>
      <ChoosePaymentAccount onRemove={onRemove} openCreateAccount={onConnectAccount} />
      <TransferCashBackFormButton onTransferSuccess={onTransferSuccess} />
    </Stack>
  );
};

export { TransferCashBackForm };
